import type { SVGProps } from 'react'

/**
 * Etz icon set — Figma "Icons" component set (27:56), re-exported 2026-07-28
 * after the Size=20 nav glyphs were normalized to a 17px optical box.
 * Path data is the exact Figma export; only stroke color is swapped to
 * currentColor so states recolor via CSS `color`. Do not hand-edit paths —
 * re-export from Figma instead.
 */

type IconProps = SVGProps<SVGSVGElement>

function baseProps(size: number, props: IconProps) {
    return {
        width: size,
        height: size,
        viewBox: `0 0 ${size} ${size}`,
        fill: 'none',
        'aria-hidden': true,
        ...props,
    } as const
}

const stroke20 = {
    stroke: 'currentColor',
    strokeWidth: 1.25,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
} as const

/* Lovable-sourced dashboard glyph (lucide LayoutDashboard, rescaled 24→20
   grid) — adopted 2026-08-01 for the Dashboard nav item. Not yet in the
   Figma Icons set (27:56); add it there on the post-reskin Etz re-sync,
   then re-export like the others. */
export function IconDashboard(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <rect x="2.5" y="2.5" width="5.83" height="7.5" rx="0.83" {...stroke20} />
            <rect x="11.67" y="2.5" width="5.83" height="4.17" rx="0.83" {...stroke20} />
            <rect x="11.67" y="10" width="5.83" height="7.5" rx="0.83" {...stroke20} />
            <rect x="2.5" y="13.33" width="5.83" height="4.17" rx="0.83" {...stroke20} />
        </svg>
    )
}

/* Donations glyph (lucide HandCoins, rescaled 24→20 grid) — adopted for the
   un-parked Donations nav item. Not yet in the Figma Icons set (27:56); add
   it there on the post-reskin Etz re-sync, then re-export like the others. */
export function IconDonations(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M9.17 12.5h1.66a1.67 1.67 0 1 0 0-3.33H8.33c-.5 0-.92.16-1.17.5L2.5 14.17"
                {...stroke20}
            />
            <path
                d="m5.83 17.5 1.34-1.17c.25-.33.66-.5 1.16-.5h3.34c.91 0 1.75-.33 2.33-1l3.83-3.66a1.667 1.667 0 0 0-2.29-2.43l-3.5 3.25"
                {...stroke20}
            />
            <path d="m1.67 13.33 5 5" {...stroke20} />
            <circle cx="13.33" cy="7.5" r="2.42" {...stroke20} />
            <circle cx="5" cy="4.17" r="2.5" {...stroke20} />
        </svg>
    )
}

/* Inbox glyph (lucide Inbox, rescaled 24→20 grid) — adopted for the payments
   inbox nav item. Not yet in the Figma Icons set (27:56); add it there on the
   post-reskin Etz re-sync, then re-export like the others. */
export function IconInbox(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path d="M18.33 10h-5l-1.66 2.5H8.33L6.67 10h-5" {...stroke20} />
            <path
                d="M4.54 4.26 1.67 10v5a1.67 1.67 0 0 0 1.66 1.67h13.34A1.67 1.67 0 0 0 18.33 15v-5l-2.87-5.74a1.67 1.67 0 0 0-1.49-.93H6.03a1.67 1.67 0 0 0-1.49.93z"
                {...stroke20}
            />
        </svg>
    )
}

export function IconHome(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M2.35019 7.45L10 1.5L17.6498 7.45V16.8C17.6498 17.2509 17.4707 17.6833 17.1519 18.0021C16.8331 18.3209 16.4007 18.5 15.9499 18.5H4.05015C3.59929 18.5 3.1669 18.3209 2.8481 18.0021C2.52929 17.6833 2.35019 17.2509 2.35019 16.8V7.45Z"
                {...stroke20}
            />
            <path d="M7.45006 18.5V10H12.5499V18.5" {...stroke20} />
        </svg>
    )
}

export function IconUsers(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M13.4 17.6502V15.9501C13.4 15.0484 13.0418 14.1835 12.4042 13.5459C11.7665 12.9083 10.9017 12.55 10 12.55H4.9C3.99826 12.55 3.13346 12.9083 2.49584 13.5459C1.85821 14.1835 1.5 15.0484 1.5 15.9501V17.6502"
                {...stroke20}
            />
            <path
                d="M7.45 9.15002C9.32777 9.15002 10.85 7.62775 10.85 5.74994C10.85 3.87212 9.32777 2.34985 7.45 2.34985C5.57223 2.34985 4.05 3.87212 4.05 5.74994C4.05 7.62775 5.57223 9.15002 7.45 9.15002Z"
                {...stroke20}
            />
            <path
                d="M18.5 17.6501V15.9501C18.4994 15.1968 18.2487 14.4649 17.7872 13.8695C17.3256 13.2741 16.6794 12.8489 15.95 12.6605"
                {...stroke20}
            />
            <path
                d="M13.4 2.46033C14.1313 2.64759 14.7796 3.07294 15.2425 3.66932C15.7054 4.2657 15.9567 4.99919 15.9567 5.75416C15.9567 6.50912 15.7054 7.24261 15.2425 7.83899C14.7796 8.43538 14.1313 8.86073 13.4 9.04799"
                {...stroke20}
            />
        </svg>
    )
}

export function IconCandle(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M12.125 6.175C12.5499 4.9 11.7 3.625 10 1.5C8.30005 3.625 7.45007 4.9 7.87506 6.175C8.30005 7.025 9.15003 7.45 10 7.45C10.85 7.45 11.7 7.025 12.125 6.175Z"
                {...stroke20}
            />
            <path d="M10 7.44995V9.14995M5.75011 9.14995H14.2499V18.5H5.75011V9.14995Z" {...stroke20} />
        </svg>
    )
}

export function IconCalendar(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M15.9499 3.19995H4.05015C3.11129 3.19995 2.35019 3.96107 2.35019 4.89995V16.8C2.35019 17.7388 3.11129 18.5 4.05015 18.5H15.9499C16.8887 18.5 17.6498 17.7388 17.6498 16.8V4.89995C17.6498 3.96107 16.8887 3.19995 15.9499 3.19995Z"
                {...stroke20}
            />
            <path d="M13.3978 1.5V5.5405" {...stroke20} />
            <path d="M6.59798 1.5V5.5405" {...stroke20} />
            {/* Figma export draws this rule to x=20.532, past the 20px frame —
                clamped to the calendar body edge; flagged for an upstream fix. */}
            <path d="M2.35019 8.29785H17.6498" {...stroke20} />
        </svg>
    )
}

export function IconUserManagement(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M10 5.74993C10 3.87139 8.4785 2.34985 6.6 2.34985C4.7215 2.34985 3.2 3.87139 3.2 5.74993C3.2 7.62848 4.7215 9.15002 6.6 9.15002C8.4785 9.15002 10 7.62848 10 5.74993Z"
                {...stroke20}
            />
            <path d="M1.5 17.6502V15.9501C1.5 14.0461 2.996 12.55 4.9 12.55H10" {...stroke20} />
            <path
                d="M15.1 12.1251C16.273 12.1251 17.225 13.0772 17.225 14.2502M15.1 12.1251C13.927 12.1251 12.975 13.0772 12.975 14.2502M15.1 12.1251V10.8501M17.225 14.2502C17.225 15.4232 16.273 16.3752 15.1 16.3752M17.225 14.2502H18.5M15.1 16.3752C13.927 16.3752 12.975 15.4232 12.975 14.2502M15.1 16.3752V17.6503M12.975 14.2502H11.7"
                {...stroke20}
            />
        </svg>
    )
}

/* Help glyph (lucide CircleHelp, rescaled 24→20 grid) — adopted 2026-08-14
   for the topbar Help & support icon; the raw lucide render (stroke 2 on a
   24 viewBox) sat visibly heavier than its stroke-1.25 Etz neighbors. Not
   yet in the Figma Icons set (27:56); add it there on the post-reskin Etz
   re-sync, then re-export like the others. */
export function IconHelp(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <circle cx="10" cy="10" r="8.33" {...stroke20} />
            <path d="M7.575 7.5a2.5 2.5 0 0 1 4.858.833c0 1.667-2.5 2.5-2.5 2.5" {...stroke20} />
            {/* Filled dot — a stroked point at 1.25 all but disappears */}
            <circle cx="10" cy="14.17" r="0.9" fill="currentColor" />
        </svg>
    )
}

export function IconSettings(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M10.187 1.5H9.813C9.36214 1.5 8.92975 1.67911 8.61095 1.99792C8.29214 2.31673 8.11304 2.74913 8.11304 3.2V3.353C8.11273 3.65112 8.03404 3.94391 7.88485 4.20201C7.73567 4.46011 7.52123 4.67444 7.26306 4.8235L6.89757 5.036C6.63914 5.18521 6.34599 5.26376 6.04759 5.26376C5.74918 5.26376 5.45604 5.18521 5.19761 5.036L5.07011 4.968C4.68003 4.74297 4.2166 4.68193 3.78155 4.79826C3.3465 4.9146 2.97538 5.19881 2.74967 5.5885L2.56267 5.9115C2.33765 6.30159 2.27661 6.76504 2.39294 7.2001C2.50927 7.63516 2.79347 8.00628 3.18316 8.232L3.31066 8.317C3.56758 8.46533 3.78122 8.67833 3.93033 8.93481C4.07945 9.19129 4.15884 9.48233 4.16063 9.779V10.2125C4.16182 10.5121 4.08384 10.8066 3.93458 11.0663C3.78532 11.3261 3.57008 11.5417 3.31066 11.6915L3.18316 11.768C2.79347 11.9937 2.50927 12.3648 2.39294 12.7999C2.27661 13.235 2.33765 13.6984 2.56267 14.0885L2.74967 14.4115C2.97538 14.8012 3.3465 15.0854 3.78155 15.2017C4.2166 15.3181 4.68003 15.257 5.07011 15.032L5.19761 14.964C5.45604 14.8148 5.74918 14.7362 6.04759 14.7362C6.34599 14.7362 6.63914 14.8148 6.89757 14.964L7.26306 15.1765C7.52123 15.3256 7.73567 15.5399 7.88485 15.798C8.03404 16.0561 8.11273 16.3489 8.11304 16.647V16.8C8.11304 17.2509 8.29214 17.6833 8.61095 18.0021C8.92975 18.3209 9.36214 18.5 9.813 18.5H10.187C10.6378 18.5 11.0702 18.3209 11.389 18.0021C11.7078 17.6833 11.887 17.2509 11.887 16.8V16.647C11.8873 16.3489 11.9659 16.0561 12.1151 15.798C12.2643 15.5399 12.4788 15.3256 12.7369 15.1765L13.1024 14.964C13.3608 14.8148 13.654 14.7362 13.9524 14.7362C14.2508 14.7362 14.544 14.8148 14.8024 14.964L14.9299 15.032C15.32 15.257 15.7834 15.3181 16.2184 15.2017C16.6535 15.0854 17.0246 14.8012 17.2503 14.4115L17.4373 14.08C17.6623 13.6899 17.7234 13.2265 17.6071 12.7914C17.4907 12.3563 17.2065 11.9852 16.8168 11.7595L16.6893 11.6915C16.4299 11.5417 16.2147 11.3261 16.0654 11.0663C15.9162 10.8066 15.8382 10.5121 15.8394 10.2125V9.7875C15.8382 9.48794 15.9162 9.19339 16.0654 8.93367C16.2147 8.67395 16.4299 8.45828 16.6893 8.3085L16.8168 8.232C17.2065 8.00628 17.4907 7.63516 17.6071 7.2001C17.7234 6.76504 17.6623 6.30159 17.4373 5.9115L17.2503 5.5885C17.0246 5.19881 16.6535 4.9146 16.2184 4.79826C15.7834 4.68193 15.32 4.74297 14.9299 4.968L14.8024 5.036C14.544 5.18521 14.2508 5.26376 13.9524 5.26376C13.654 5.26376 13.3608 5.18521 13.1024 5.036L12.7369 4.8235C12.4788 4.67444 12.2643 4.46011 12.1151 4.20201C11.9659 3.94391 11.8873 3.65112 11.887 3.353V3.2C11.887 2.74913 11.7078 2.31673 11.389 1.99792C11.0702 1.67911 10.6378 1.5 10.187 1.5Z"
                {...stroke20}
            />
            <path
                d="M10 12.55C11.4083 12.55 12.5499 11.4083 12.5499 9.99995C12.5499 8.59162 11.4083 7.44995 10 7.44995C8.5917 7.44995 7.45006 8.59162 7.45006 9.99995C7.45006 11.4083 8.5917 12.55 10 12.55Z"
                {...stroke20}
            />
        </svg>
    )
}

export function IconLightbulb(props: IconProps) {
    return (
        <svg {...baseProps(20, props)}>
            <path
                d="M7.31586 15.8158V12.2368C7.13692 11.3421 6.60009 10.6263 5.97379 10C4.81066 8.83684 4.63171 7.76316 4.63171 6.86842C4.63171 3.90684 7.0385 1.5 10 1.5C12.9615 1.5 15.3683 3.90684 15.3683 6.86842C15.3683 7.76316 15.1894 8.83684 14.0262 10C13.3999 10.6263 12.8631 11.3421 12.6842 12.2368V15.8158"
                {...stroke20}
            />
            <path d="M7.31586 15.8157H12.6841M8.21057 18.4999H11.7894" {...stroke20} />
        </svg>
    )
}

export function IconLogOut({ size = 16, ...props }: IconProps & { size?: 16 | 20 }) {
    if (size === 20) {
        const s = { ...stroke20, strokeWidth: 1.66667 }
        return (
            <svg {...baseProps(20, props)}>
                <path
                    d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5"
                    {...s}
                />
                <path d="M13.3333 14.1666L17.5 9.99998L13.3333 5.83331" {...s} />
                <path d="M17.5 10H7.5" {...s} />
            </svg>
        )
    }
    const s = { ...stroke20, strokeWidth: 1.33333 }
    return (
        <svg {...baseProps(16, props)}>
            <path
                d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6"
                {...s}
            />
            <path d="M10.6667 11.3334L14 8.00002L10.6667 4.66669" {...s} />
            <path d="M14 8H6" {...s} />
        </svg>
    )
}

export function IconChevronRight(props: IconProps) {
    return (
        <svg {...baseProps(16, props)}>
            <path
                d="M5.57576 12.8486L10.4242 7.99997L5.57576 3.15137"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}
